import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Card,
  Col,
  Row,
  Input,
  Button,
  message,
  Spin,
  FloatButton,
  notification,
  Modal,
  Descriptions,
} from "antd";
import {
  BellOutlined,
  BellFilled,
  BulbOutlined,
  MoonOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import {
  KitchenDisplayStatus,
  DeviceType,
} from "../../../../configs/constants";

import KitchenAwaitingOrders from "./KitchenAwaitingOrders";
import KitchenPreparingOrders from "./KitchenPreparingOrders";
import "./KitchenExternalDisplay.css";

import {
  bindDevice,
  getDeviceStatus,
  refreshDeviceToken,
  getKitchenOrders,
} from "../../../../network/network";

import { generateFingerprint } from "../../../../utils/device/fingerprint";

import {
  clearDeviceAuth,
  saveDeviceAuth,
  getDeviceToken,
  getDeviceAuth,
} from "../../../../utils/device/deviceAuth";

import { getEcho, resetEcho } from "../../../../bootstrap/echo";
import { setEchoAuthToken } from "../../../../bootstrap/echoAuth";
import { useTranslation } from "react-i18next";
import {
  handleApiError,
  showAppError,
} from "../../../../utils/errorHandler";
import { showWarningMessage } from "../../../../utils/antdMessage";

const DEVICE_TYPE = DeviceType.Kds;
const PRIMARY = "var(--color-primary)";

const toOrderTypeName = (raw) => {
  if (!raw) return "-";

  const value =
    typeof raw === "string"
      ? raw
      : typeof raw === "object"
        ? raw?.name || raw?.value
        : "";

  if (!value) return "-";

  const str = String(value).toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const normalizeSocketOrder = (data) => {
  return {
    id: data?.order_id ?? data?.id,
    order_id: data?.order_number || data?.order_id || "-",
    created_at: data?.created_at || new Date().toISOString(),
    order_type: { name: toOrderTypeName(data?.order_type) },
    kitchen_status: data?.kitchen_status,
    items: Array.isArray(data?.items)
      ? data.items.map((it) => ({
          id: it?.id,
          name: it?.name,
          name_en: it?.name_en,
          quantity: it?.quantity ?? 1,
          tags: Array.isArray(it?.tags) ? it.tags : [],
        }))
      : [],
  };
};

const uniqAppendById = (list, order) => {
  if (!order?.id) return list;
  if (list.some((o) => o?.id === order.id)) return list;
  return [...list, order];
};

const emptyText = (value) => {
  if (value === undefined || value === null || value === "") return "-";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    return (
      value.name ||
      value.name_en ||
      value.name_ar ||
      value.business_name ||
      value.email ||
      value.uid ||
      value.id ||
      "-"
    );
  }

  return "-";
};

const KitchenExternalDisplay = () => {
  const { t } = useTranslation();
  const [checkingDevice, setCheckingDevice] = useState(true);
  const [paired, setPaired] = useState(false);

  const [pairCode, setPairCode] = useState("");
  const [pairingLoading, setPairingLoading] = useState(false);

  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("kds_sound_enabled");
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("kds_dark_mode");
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [orderStatus, setOrderStatus] = useState(KitchenDisplayStatus.Awaiting);

  const [awaitingOrders, setAwaitingOrders] = useState([]);
  const [preparingOrders, setPreparingOrders] = useState([]);

  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [kdsInfo, setKdsInfo] = useState({
    deviceName: "-",
    branch: "-",
    user: "-",
  });

  const [awaitingLoading, setAwaitingLoading] = useState(false);
  const [preparingLoading, setPreparingLoading] = useState(false);

  const awaitingCount = awaitingOrders.length;
  const preparingCount = preparingOrders.length;

  const subscribedChannelRef = useRef(null);
  const bellRef = useRef(null);
  const errorToneRef = useRef(null);
  const soundEnabledRef = useRef(soundEnabled);
  const activeAudioRef = useRef(new Set());
  const audioWarningShownRef = useRef(false);

  const initialLoadedRef = useRef(false);
  const loadingInitialRef = useRef(false);

  const onChangeTab = (key) => setOrderStatus(key);

  const onUnauthorized = useCallback(() => {
    try {
      const prev = subscribedChannelRef.current;
      if (prev) window.Echo?.leave?.(prev);
    } catch {}

    subscribedChannelRef.current = null;

    resetEcho();
    clearDeviceAuth(DEVICE_TYPE);

    setPaired(false);
    setPairCode("");
    setAwaitingOrders([]);
    setPreparingOrders([]);

    initialLoadedRef.current = false;
    loadingInitialRef.current = false;

    showWarningMessage("Device unpaired • تم إلغاء ربط الجهاز");
  }, []);

  useEffect(() => {
    let isEffectActive = true;

    const verify = async () => {
      try {
        const token = getDeviceToken(DEVICE_TYPE);

        if (!token) {
          if (isEffectActive) {
            setPaired(false);
            setCheckingDevice(false);
          }
          return;
        }

        const statusRes = await getDeviceStatus(DEVICE_TYPE);
        if (!isEffectActive) return;

        const ok =
          statusRes?.data?.success === true &&
          statusRes?.data?.data?.is_active !== false;

        if (ok) {
          if (isEffectActive) {
            setPaired(true);
            setCheckingDevice(false);
          }
          return;
        }

        throw new Error("Status invalid");
      } catch {
        if (!isEffectActive) return;

        try {
          const fingerprint = await generateFingerprint();
          if (!isEffectActive) return;

          const refreshRes = await refreshDeviceToken(
            { device_fingerprint: fingerprint },
            DEVICE_TYPE,
          );
          if (!isEffectActive) return;

          const refreshOk = refreshRes?.data?.success === true;
          const auth = refreshRes?.data?.data;

          if (!refreshOk || !auth?.token) {
            if (isEffectActive) {
              handleApiError(refreshRes, t);
              onUnauthorized();
              setCheckingDevice(false);
            }
            return;
          }

          saveDeviceAuth({ ...auth, device_type: DEVICE_TYPE });

          if (isEffectActive) {
            setPaired(true);
            setCheckingDevice(false);
          }
        } catch (error) {
          if (isEffectActive) {
            handleApiError(error, t);
            onUnauthorized();
            setCheckingDevice(false);
          }
        }
      }
    };

    verify();

    return () => {
      isEffectActive = false;
    };
  }, [onUnauthorized, t]);

  const loadInitialOrders = useCallback(async () => {
    if (initialLoadedRef.current) return;
    if (loadingInitialRef.current) return;

    loadingInitialRef.current = true;
    initialLoadedRef.current = true;

    try {
      setAwaitingLoading(true);
      setPreparingLoading(true);

      const [awaitingRes, preparingRes] = await Promise.all([
        getKitchenOrders({
          kitchen_status: KitchenDisplayStatus.Awaiting,
          per_page: 100,
        }),
        getKitchenOrders({
          kitchen_status: KitchenDisplayStatus.Preparing,
          per_page: 100,
        }),
      ]);

      const failedResponse = [awaitingRes, preparingRes].find(
        (response) => response?.data?.success === false,
      );

      if (failedResponse) {
        handleApiError(failedResponse, t);
        setAwaitingOrders([]);
        setPreparingOrders([]);
        return;
      }

      const awaitingList = awaitingRes?.data?.data?.transactions || [];
      const preparingList = preparingRes?.data?.data?.transactions || [];

      setAwaitingOrders(Array.isArray(awaitingList) ? awaitingList : []);
      setPreparingOrders(Array.isArray(preparingList) ? preparingList : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized();
        return;
      }
      handleApiError(err, t);
      setAwaitingOrders([]);
      setPreparingOrders([]);
    } finally {
      loadingInitialRef.current = false;
      setAwaitingLoading(false);
      setPreparingLoading(false);
    }
  }, [onUnauthorized, t]);

  useEffect(() => {
    if (!paired) return;
    loadInitialOrders();
  }, [paired, loadInitialOrders]);

  const getInfoOrder = useCallback(() => {
    return [...awaitingOrders, ...preparingOrders].find((order) => order?.id);
  }, [awaitingOrders, preparingOrders]);

  const showInfoModal = useCallback(async () => {
    setInfoModalVisible(true);
    setInfoLoading(true);

    const nextInfo = {
      deviceName: "-",
      branch: "-",
      user: "-",
    };

    try {
      const statusRes = await getDeviceStatus(DEVICE_TYPE);

      if (statusRes?.data?.success === false) {
        handleApiError(statusRes, t);
      }

      const statusData = statusRes?.data?.data || {};

      nextInfo.deviceName = emptyText(statusData.device_name);

      nextInfo.branch = emptyText(statusData.branch);
    } catch (err) {
      if (err?.response?.status === 401) {
        setInfoLoading(false);
        onUnauthorized();
        return;
      }
      handleApiError(err, t);
    }

    const infoOrder = getInfoOrder();

    if (infoOrder?.id) {
      nextInfo.branch = emptyText(infoOrder.branch);

      nextInfo.user = emptyText(infoOrder.user);
    }

    setKdsInfo(nextInfo);
    setInfoLoading(false);
  }, [getInfoOrder, onUnauthorized, t]);

  const reportAudioError = useCallback((error, toneName) => {
    console.warn(`KDS ${toneName} sound failed to play`, error);

    if (audioWarningShownRef.current) return;
    audioWarningShownRef.current = true;

    showWarningMessage(
      error?.name === "NotAllowedError"
        ? "KDS sound is blocked by the browser. Click anywhere on the KDS, or turn sound off and on."
        : "KDS sound could not play. Check the device volume and sound files.",
      {
        key: "kds-audio-playback-error",
        duration: 6,
      },
    );
  }, []);

  const primeTone = useCallback(async (toneRef) => {
    const audio = toneRef.current;
    if (!audio || !audio.paused) return true;

    const previousVolume = audio.volume;

    try {
      audio.volume = 0;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audioWarningShownRef.current = false;
      return true;
    } catch (error) {
      console.warn("KDS sound could not be initialized", error);
      return false;
    } finally {
      audio.volume = previousVolume;
    }
  }, []);

  const playTone = useCallback(
    async (toneRef, toneName, { force = false } = {}) => {
      if (!force && !soundEnabledRef.current) return false;

      const template = toneRef.current;
      if (!template) return false;

      const audio = template.paused ? template : template.cloneNode(true);
      const isClone = audio !== template;

      const cleanupClone = () => {
        if (!isClone) return;
        audio.removeEventListener("ended", cleanupClone);
        audio.removeEventListener("error", cleanupClone);
        activeAudioRef.current.delete(audio);
      };

      if (isClone) {
        activeAudioRef.current.add(audio);
        audio.addEventListener("ended", cleanupClone, { once: true });
        audio.addEventListener("error", cleanupClone, { once: true });
      }

      try {
        audio.currentTime = 0;
        await audio.play();
        audioWarningShownRef.current = false;
        return true;
      } catch (error) {
        cleanupClone();
        reportAudioError(error, toneName);
        return false;
      }
    },
    [reportAudioError],
  );

  const stopAllTones = useCallback(() => {
    [bellRef.current, errorToneRef.current].forEach((audio) => {
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
    });

    activeAudioRef.current.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    activeAudioRef.current.clear();
  }, []);

  const toggleSound = () => {
    const next = !soundEnabledRef.current;

    soundEnabledRef.current = next;
    setSoundEnabled(next);
    localStorage.setItem("kds_sound_enabled", JSON.stringify(next));

    if (next) {
      void primeTone(errorToneRef);
      void playTone(bellRef, "notification", { force: true });
    } else {
      stopAllTones();
    }
  };

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    bellRef.current = new Audio("/sounds/bell.mp3");
    bellRef.current.preload = "auto";
    bellRef.current.load();

    errorToneRef.current = new Audio("/sounds/error.mp3");
    errorToneRef.current.preload = "auto";
    errorToneRef.current.load();

    return () => {
      stopAllTones();
      bellRef.current = null;
      errorToneRef.current = null;
    };
  }, [stopAllTones]);

  useEffect(() => {
    if (!soundEnabled) return undefined;

    const removeUnlockListeners = () => {
      document.removeEventListener("pointerdown", unlockAudio, true);
      document.removeEventListener("keydown", unlockAudio, true);
    };

    const unlockAudio = () => {
      removeUnlockListeners();
      void primeTone(bellRef);
      void primeTone(errorToneRef);
    };

    document.addEventListener("pointerdown", unlockAudio, true);
    document.addEventListener("keydown", unlockAudio, true);

    return removeUnlockListeners;
  }, [soundEnabled, primeTone]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("kds_dark_mode", JSON.stringify(next));
      return next;
    });
  };

  const showCancelledOrderPopup = useCallback((orderCode) => {
    notification.open({
      message: (
        <div
          style={{
            fontWeight: 800,
            fontSize: 16,
            color: "var(--color-danger-hover)",
          }}
        >
          Order Cancelled
        </div>
      ),
      description: (
        <div>
          <div
            style={{
              fontSize: 14,
              color: "var(--color-text)",
              marginBottom: 4,
            }}
          >
            Order <strong>{orderCode || "-"}</strong> has been removed from
            kitchen.
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--color-text)",
              direction: "rtl",
              textAlign: "right",
            }}
          >
            تم إلغاء الطلب وإزالته من شاشة المطبخ
          </div>
        </div>
      ),
      icon: (
        <CloseCircleOutlined
          style={{ color: "var(--color-danger)", fontSize: 22 }}
        />
      ),
      placement: "topRight",
      duration: 5,
      style: {
        borderRadius: 16,
        border: "1px solid var(--color-danger-shadow)",
        boxShadow: "0 14px 40px var(--color-danger-shadow)",
        background: "var(--color-danger-bg)",
      },
    });
  }, []);

  useEffect(() => {
    if (!paired) return;

    const auth = getDeviceAuth?.(DEVICE_TYPE);
    const branchId = auth?.branch_id;
    const myDeviceUid = auth?.device_uid;

    const token = getDeviceToken(DEVICE_TYPE);
    const echo = getEcho();

    if (!branchId || !token || !echo || !window.Echo) return;

    setEchoAuthToken(token);

    const channelName = `pos.${branchId}.kds`;

    if (subscribedChannelRef.current === channelName) return;

    try {
      const prev = subscribedChannelRef.current;
      if (prev) window.Echo.leave(prev);
    } catch {}

    subscribedChannelRef.current = channelName;

    const kdsChannel = window.Echo.private(channelName);

    kdsChannel.listen(".new-order", (data) => {
      const order = normalizeSocketOrder(data);

      if (!order?.id) return;

      const status = data?.kitchen_status;

      if (status === KitchenDisplayStatus.Preparing) {
        setPreparingOrders((prev) => uniqAppendById(prev, order));
      } else {
        setAwaitingOrders((prev) => uniqAppendById(prev, order));
      }

      message.open({
        type: "success",
        content: `New order: ${order.order_id}`,
        duration: 3,
      });

      void playTone(bellRef, "new order");
    });

    kdsChannel.listen(".update-order", (data) => {
      const order = normalizeSocketOrder(data);

      if (!order?.id) return;

      const status = data?.kitchen_status;

      setAwaitingOrders((prev) => prev.filter((o) => o?.id !== order.id));
      setPreparingOrders((prev) => prev.filter((o) => o?.id !== order.id));

      if (status === KitchenDisplayStatus.Preparing) {
        setPreparingOrders((prev) => uniqAppendById(prev, order));
      } else if (status === KitchenDisplayStatus.Awaiting) {
        setAwaitingOrders((prev) => uniqAppendById(prev, order));
      }

      message.open({
        type: "info",
        content: `Order updated: ${order.order_id}`,
        duration: 3,
      });

      void playTone(bellRef, "updated order");
    });

    kdsChannel.listen(".delete-order", (data) => {
      const socketOrderId = data?.order_id ?? data?.id;
      const orderCode = data?.order_number || data?.order_id || data?.id;

      if (!socketOrderId) return;

      setAwaitingOrders((prev) =>
        prev.filter((order) => order.id !== socketOrderId),
      );

      setPreparingOrders((prev) =>
        prev.filter((order) => order.id !== socketOrderId),
      );

      showCancelledOrderPopup(orderCode);

      void playTone(errorToneRef, "cancelled order");
    });

    kdsChannel.listen(".device-unbound", (data) => {
      if (data?.device_type && data.device_type !== DEVICE_TYPE) return;

      if (data?.device_uid && myDeviceUid && data.device_uid === myDeviceUid) {
        onUnauthorized();
      }
    });

    return () => {
      window.Echo?.leave?.(channelName);
      if (subscribedChannelRef.current === channelName) {
        subscribedChannelRef.current = null;
      }
    };
  }, [paired, onUnauthorized, playTone, showCancelledOrderPopup]);

  const moveAwaitingToPreparing = useCallback((orderId) => {
    if (!orderId) return;

    setAwaitingOrders((prevAwaiting) => {
      const found = prevAwaiting.find((o) => o?.id === orderId);
      if (!found) return prevAwaiting;

      const nextAwaiting = prevAwaiting.filter((o) => o?.id !== orderId);

      setPreparingOrders((prevPreparing) =>
        uniqAppendById(prevPreparing, found),
      );

      return nextAwaiting;
    });
  }, []);

  const removeFromPreparing = useCallback((orderId) => {
    if (!orderId) return;
    setPreparingOrders((prev) => prev.filter((o) => o?.id !== orderId));
  }, []);

  const handlePair = async () => {
    const code = pairCode.trim();

    if (!/^\d{6}$/.test(code)) {
      showAppError("Enter valid 6-digit code • أدخل رمز صحيح من 6 أرقام");
      return;
    }

    try {
      setPairingLoading(true);

      const fingerprint = await generateFingerprint();

      const res = await bindDevice({
        pairing_code: code,
        device_fingerprint: fingerprint,
      });

      const ok = res?.data?.success === true;
      const auth = res?.data?.data;

      if (res?.data?.success === false) {
        handleApiError(res, t);
        return;
      }

      if (!ok || !auth?.token) {
        showAppError("Invalid/expired code • الرمز غير صالح أو منتهي");
        return;
      }

      if (auth?.device_type && auth.device_type !== DEVICE_TYPE) {
        showAppError(
          "This code is not for Kitchen (KDS) • هذا الرمز ليس لجهاز المطبخ",
        );
        return;
      }

      saveDeviceAuth({ ...auth, device_type: DEVICE_TYPE });

      message.success("Paired successfully • تم الربط بنجاح");

      resetEcho();

      initialLoadedRef.current = false;
      loadingInitialRef.current = false;
      setAwaitingOrders([]);
      setPreparingOrders([]);

      setPaired(true);
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setPairingLoading(false);
    }
  };

  const theme = useMemo(() => {
    if (isDarkMode) {
      return {
        pageBackground:
          "linear-gradient(180deg, var(--color-heading) 0%, var(--color-heading) 45%, var(--color-heading) 100%)",
        contentBg: "var(--color-heading)",
        contentBorder: "1px solid var(--color-primary-shadow)",
        contentShadow: "0 12px 30px var(--color-shadow)",
        tabInactiveBg: "var(--color-heading)",
        tabInactiveText: "var(--color-bg)",
        tabBorder: "1px solid var(--color-primary-shadow)",
      };
    }

    return {
      pageBackground:
        "linear-gradient(180deg, var(--color-bg) 0%, var(--color-white) 100%)",
      contentBg: "var(--color-white)",
      contentBorder: "1px solid var(--color-bg)",
      contentShadow: "0 10px 30px var(--color-shadow-soft)",
      tabInactiveBg: "var(--color-white)",
      tabInactiveText: "var(--color-text)",
      tabBorder: "1px solid var(--color-bg)",
    };
  }, [isDarkMode]);

  const pageWrapStyle = useMemo(
    () => ({
      minHeight: "100vh",
      padding: 20,
      background: theme.pageBackground,
      transition: "all 0.3s ease",
    }),
    [theme],
  );

  const contentWrapStyle = useMemo(
    () => ({
      margin: "0 auto",
      borderRadius: 22,
      background: theme.contentBg,
      boxShadow: theme.contentShadow,
      border: theme.contentBorder,
      padding: 14,
      transition: "all 0.3s ease",
    }),
    [theme],
  );

  const pairingBox = (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
        backgroundImage: "url('/images/kitchen-bg-img.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(var(--color-overlay-strong), var(--color-overlay-strong))",
        }}
      />

      <Card
        style={{
          width: 420,
          borderRadius: 18,
          border: "none",
          zIndex: 2,
          backdropFilter: "blur(8px)",
          background: "var(--color-white-soft)",
          boxShadow: "0 25px 60px var(--color-overlay-soft)",
        }}
        title={
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>
              Pair Kitchen Device
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, direction: "rtl" }}>
              ربط جهاز المطبخ
            </div>
          </div>
        }
      >
        <Input
          value={pairCode}
          onChange={(e) => setPairCode(e.target.value)}
          placeholder="123456"
          maxLength={6}
          inputMode="numeric"
          style={{
            marginBottom: 12,
            borderRadius: 10,
            height: 44,
            fontSize: 16,
            letterSpacing: 3,
            textAlign: "center",
          }}
        />

        <Button
          type="primary"
          block
          loading={pairingLoading}
          onClick={handlePair}
          style={{
            height: 44,
            borderRadius: 10,
            fontWeight: 700,
            background: PRIMARY,
            borderColor: PRIMARY,
          }}
        >
          Pair • ربط
        </Button>
      </Card>
    </div>
  );

  if (checkingDevice) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: theme.pageBackground,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!paired) return pairingBox;

  return (
    <div
      className={`kds-screen${isDarkMode ? " kds-screen--dark" : ""}`}
      style={pageWrapStyle}
    >
      <div style={{ margin: "0 auto 14px auto" }}>
        <Row gutter={[12, 12]} justify="center">
          <Col xs={12} sm={8} md={6} lg={5}>
            <div
              onClick={() => onChangeTab(KitchenDisplayStatus.Awaiting)}
              style={{
                borderRadius: 14,
                cursor: "pointer",
                padding: "10px 12px",
                textAlign: "center",
                background:
                  orderStatus === KitchenDisplayStatus.Awaiting
                    ? "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary) 100%)"
                    : theme.tabInactiveBg,
                color:
                  orderStatus === KitchenDisplayStatus.Awaiting
                    ? "var(--color-white)"
                    : theme.tabInactiveText,
                border: theme.tabBorder,
                transition: "all 0.3s ease",
              }}
            >
              <div style={{ fontWeight: 800 }}>Awaiting</div>
              <div style={{ direction: "rtl" }}>بانتظار</div>
              <div style={{ marginTop: 6, fontSize: 22, fontWeight: 900 }}>
                {awaitingLoading ? "…" : awaitingCount}
              </div>
            </div>
          </Col>

          <Col xs={12} sm={8} md={6} lg={5}>
            <div
              onClick={() => onChangeTab(KitchenDisplayStatus.Preparing)}
              style={{
                borderRadius: 14,
                cursor: "pointer",
                padding: "10px 12px",
                textAlign: "center",
                background:
                  orderStatus === KitchenDisplayStatus.Preparing
                    ? "linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary) 100%)"
                    : theme.tabInactiveBg,
                color:
                  orderStatus === KitchenDisplayStatus.Preparing
                    ? "var(--color-white)"
                    : theme.tabInactiveText,
                border: theme.tabBorder,
                transition: "all 0.3s ease",
              }}
            >
              <div style={{ fontWeight: 800 }}>Preparing</div>
              <div style={{ direction: "rtl" }}>تحضير</div>
              <div style={{ marginTop: 6, fontSize: 22, fontWeight: 900 }}>
                {preparingLoading ? "…" : preparingCount}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <FloatButton
        shape="circle"
        type="default"
        icon={<InfoCircleOutlined />}
        onClick={showInfoModal}
        tooltip="Info • معلومات"
        style={{
          right: 155,
          top: 35,
        }}
      />

      <FloatButton
        shape="circle"
        type={isDarkMode ? "primary" : "default"}
        icon={isDarkMode ? <MoonOutlined /> : <BulbOutlined />}
        onClick={toggleTheme}
        tooltip={isDarkMode ? "Dark Mode" : "Light Mode"}
        style={{
          right: 95,
          top: 35,
        }}
      />

      <FloatButton
        shape="circle"
        type={soundEnabled ? "primary" : "default"}
        icon={soundEnabled ? <BellFilled /> : <BellOutlined />}
        onClick={toggleSound}
        tooltip={soundEnabled ? "Sound ON" : "Sound OFF"}
        style={{
          right: 35,
          top: 35,
        }}
      />

      <Modal
        open={infoModalVisible}
        title="Kitchen Info • معلومات المطبخ"
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <Button
            type="default"
            key="close"
            onClick={() => setInfoModalVisible(false)}
          >
            Close • إغلاق
          </Button>,
        ]}
        centered
      >
        <Spin spinning={infoLoading}>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Device Name">
              {kdsInfo.deviceName}
            </Descriptions.Item>
            <Descriptions.Item label="Branch">
              {kdsInfo.branch}
            </Descriptions.Item>
            <Descriptions.Item label="User">{kdsInfo.user}</Descriptions.Item>
          </Descriptions>
        </Spin>
      </Modal>

      <div style={contentWrapStyle}>
        <div
          style={{
            display:
              orderStatus === KitchenDisplayStatus.Awaiting
                ? "block"
                : "none",
          }}
        >
          <KitchenAwaitingOrders
            loading={awaitingLoading}
            orders={awaitingOrders}
            onUnauthorized={onUnauthorized}
            onAccepted={(orderId) => moveAwaitingToPreparing(orderId)}
            isDarkMode={isDarkMode}
          />
        </div>

        <div
          style={{
            display:
              orderStatus === KitchenDisplayStatus.Preparing
                ? "block"
                : "none",
          }}
        >
          <KitchenPreparingOrders
            loading={preparingLoading}
            orders={preparingOrders}
            onUnauthorized={onUnauthorized}
            onCooked={(orderId) => removeFromPreparing(orderId)}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
    </div>
  );
};

export default KitchenExternalDisplay;
